import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { date, dateTime, fmtSerial, money, moneyShort } from "@/lib/format";
import { METHOD_LABEL, type ZReport } from "@/lib/types";
import { ZPrint } from "@/components/ReceiptPrint";
import { Button, cx } from "@/components/ui";
import { Card, PageHead, td, th } from "@/components/Layout";
import { rangeFor } from "./Dashboard";
import { ProfitByProduct } from "@/components/Insights";
import { useShop } from "@/lib/till-context";

type Report = {
  from: number;
  to: number;
  totals: { sales: number; takings: number; refunds: number; discounts: number };
  z: ZReport[];
  products: { name: string; qty: number; amount: number }[];
  cashiers: { name: string; sales: number; takings: number; discounts: number; refunds: number }[];
  methods: Record<string, { count: number; amount: number }>;
  serials: { serial: string; name: string; sale: string; at: number; staff: string; warrantyMonths: number | null }[];
  sales: { id: string; number: string; at: number; staff: string; total: number; refunded: number; items: number; methods: string[] }[];
};

const TABS = ["Z reports", "Sales", "By product", "By cashier", "Serials sold", "Payments"] as const;

export function Reports() {
  const [r, setR] = useState("week");
  const [tab, setTab] = useState<(typeof TABS)[number]>("Z reports");
  const shop = useShop();
  const [print, setPrint] = useState<ZReport | null>(null);
  const q = useQuery({ queryKey: ["reports", r], queryFn: () => { const { from, to } = rangeFor(r); return api<Report>(till(`/admin/reports?from=${from}&to=${to}`)); } });
  const d = q.data;
  const csv = () => {
    if (!d) return;
    const rows: (string | number)[][] = [["Sale", "Date", "Cashier", "Items", "Total", "Refunded", "Paid by"], ...d.sales.map((s) => [s.number, new Date(s.at).toISOString(), s.staff, s.items, (s.total / 100).toFixed(2), (s.refunded / 100).toFixed(2), s.methods.join("+")])];
    const blob = new Blob([rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `till-sales-${r}.csv`;
    a.click();
  };
  return (
    <>
      <PageHead title="Reports" sub={d ? `${date(d.from)} – ${date(d.to)} · all tills` : undefined}>
        {[["today", "Today"], ["week", "Last 7 days"], ["month", "Last 30 days"]].map(([k, l]) => (
          <Button key={k} tone={r === k ? "dark" : "light"} onClick={() => setR(k!)}>
            {l}
          </Button>
        ))}
        <Button onClick={csv} disabled={!d}>
          Export CSV
        </Button>
      </PageHead>
      {!d ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            {[["Takings", moneyShort(d.totals.takings)], ["Sales", String(d.totals.sales)], ["Discounts", moneyShort(d.totals.discounts)], ["Refunded", moneyShort(d.totals.refunds)]].map(([l, v]) => (
              <div key={l} className="border border-slate-200 bg-white p-4">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{l}</span>
                <div className="font-mono text-2xl font-bold">{v}</div>
              </div>
            ))}
          </div>
          <ProfitByProduct />
          <div className="no-scrollbar flex overflow-x-auto border-b border-slate-200">
            {TABS.filter((t) => t !== "Serials sold" || shop.serials).map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)} className={cx("h-11 whitespace-nowrap border-b-[3px] px-4 text-sm", tab === t ? "border-brand font-bold" : "border-transparent text-slate-500")}>
                {t}
              </button>
            ))}
          </div>
          <Card>
            <div className="overflow-x-auto">
              {tab === "Z reports" ? (
                <Table head={["Shift", "Till · cashier", "Opened – closed", "Sales", "Takings", "Cash expected", "Counted", "Over / short", "Signed off", ""]}>
                  {d.z.map((z) => (
                    <tr key={z.id}>
                      <td className={cx(td, "font-mono")}>{z.number}</td>
                      <td className={td}>{z.tillName} · {z.staffName}</td>
                      <td className={cx(td, "text-slate-500")}>{dateTime(z.openedAt)} – {z.closedAt ? dateTime(z.closedAt) : "open"}</td>
                      <td className={cx(td, "font-mono")}>{z.summary?.sales ?? "-"}</td>
                      <td className={cx(td, "font-mono")}>{moneyShort(z.summary?.takings)}</td>
                      <td className={cx(td, "font-mono")}>{money(z.expected)}</td>
                      <td className={cx(td, "font-mono")}>{money(z.counted)}</td>
                      <td className={td}>
                        <span className={cx("px-2 py-0.5 font-mono font-bold", !z.difference ? "bg-brand-tint text-brand-ink" : z.difference < 0 ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-900")}>{!z.difference ? "Exact" : money(z.difference, { sign: true })}</span>
                      </td>
                      <td className={td}>{z.approvedBy ?? "-"}</td>
                      <td className={td}>
                        <button type="button" className="text-[13px] font-semibold text-brand" onClick={() => { setPrint(z); setTimeout(() => window.print(), 100); }}>
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!d.z.length ? <EmptyRow cols={10} /> : null}
                </Table>
              ) : tab === "Sales" ? (
                <Table head={["Sale", "When", "Cashier", "Items", "Total", "Refunded", "Paid by"]}>
                  {d.sales.map((s) => (
                    <tr key={s.id}>
                      <td className={cx(td, "font-mono")}>{s.number}</td>
                      <td className={cx(td, "text-slate-500")}>{dateTime(s.at)}</td>
                      <td className={td}>{s.staff}</td>
                      <td className={cx(td, "font-mono")}>{s.items}</td>
                      <td className={cx(td, "font-mono")}>{money(s.total)}</td>
                      <td className={cx(td, "font-mono")}>{s.refunded ? money(s.refunded) : "-"}</td>
                      <td className={td}>{s.methods.map((m) => METHOD_LABEL[m as keyof typeof METHOD_LABEL] ?? m).join(" + ")}</td>
                    </tr>
                  ))}
                  {!d.sales.length ? <EmptyRow cols={7} /> : null}
                </Table>
              ) : tab === "By product" ? (
                <Table head={["Product", "Sold", "Takings"]}>
                  {d.products.map((p) => (
                    <tr key={p.name}>
                      <td className={td}>{p.name}</td>
                      <td className={cx(td, "font-mono")}>{p.qty}</td>
                      <td className={cx(td, "font-mono")}>{money(p.amount)}</td>
                    </tr>
                  ))}
                  {!d.products.length ? <EmptyRow cols={3} /> : null}
                </Table>
              ) : tab === "By cashier" ? (
                <Table head={["Cashier", "Sales", "Takings", "Discounts", "Refunded"]}>
                  {d.cashiers.map((c) => (
                    <tr key={c.name}>
                      <td className={cx(td, "font-semibold")}>{c.name}</td>
                      <td className={cx(td, "font-mono")}>{c.sales}</td>
                      <td className={cx(td, "font-mono")}>{money(c.takings)}</td>
                      <td className={cx(td, "font-mono")}>{money(c.discounts)}</td>
                      <td className={cx(td, "font-mono")}>{money(c.refunds)}</td>
                    </tr>
                  ))}
                  {!d.cashiers.length ? <EmptyRow cols={5} /> : null}
                </Table>
              ) : tab === "Serials sold" ? (
                <Table head={["IMEI / serial", "Product", "Sale", "When", "Cashier", "Warranty"]}>
                  {d.serials.map((s) => (
                    <tr key={s.serial + s.sale}>
                      <td className={cx(td, "font-mono font-bold")}>{fmtSerial(s.serial)}</td>
                      <td className={td}>{s.name}</td>
                      <td className={cx(td, "font-mono")}>{s.sale}</td>
                      <td className={cx(td, "text-slate-500")}>{dateTime(s.at)}</td>
                      <td className={td}>{s.staff}</td>
                      <td className={td}>{s.warrantyMonths ? `${s.warrantyMonths} months` : "-"}</td>
                    </tr>
                  ))}
                  {!d.serials.length ? <EmptyRow cols={6} /> : null}
                </Table>
              ) : (
                <Table head={["Method", "Payments", "Amount"]}>
                  {Object.entries(d.methods).map(([m, v]) => (
                    <tr key={m}>
                      <td className={td}>{METHOD_LABEL[m as keyof typeof METHOD_LABEL] ?? m}</td>
                      <td className={cx(td, "font-mono")}>{v.count}</td>
                      <td className={cx(td, "font-mono")}>{money(v.amount)}</td>
                    </tr>
                  ))}
                  {!Object.keys(d.methods).length ? <EmptyRow cols={3} /> : null}
                </Table>
              )}
            </div>
          </Card>
        </div>
      )}
      {print ? <ZPrint z={print} /> : null}
    </>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={i} className={th}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
const EmptyRow = ({ cols }: { cols: number }) => (
  <tr>
    <td colSpan={cols} className={cx(td, "py-6 text-center text-slate-500")}>
      Nothing in this period.
    </td>
  </tr>
);
